import { type SyntheticEvent, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useHydrated } from 'remix-utils';
import { motion, type Variants } from 'framer-motion';
import type { ButtonProps } from '@archipro-design/aria';
import {
    Button,
    Flex,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import {
    Download2Line,
    HeartFill,
    HeartLine,
    PinterestFill,
    PriceTag3Line,
    ReseneOpenPicker,
} from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import { Sparkles } from 'lucide-react';
import { useBoostControl } from '@modules/page-builder/component/common/upscale-warning/boost-control-context';
import { HoverAction } from '../../common/hover-action/HoverAction';
import * as S from './HoverButtonsImage.style';
import { useProductTagging } from '~/modules/product-tagging/context';
import { useCanDownloadImages } from '@modules/user';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import { useFeatureFlaggedPaintPickerContext } from '@modules/paint-picker-new/hooks/useFeatureFlaggedPaintPickerContext';
import { useFeature } from '~/modules/root';
import { HINT_ANIMATION_DURATION } from '@modules/paint-picker-new/config';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { buildPinterestShareUrl } from '~/modules/page-builder/util/build-pinterest-share-url';
import { useProfessionalData } from '~/modules/professional/hook/use-professional-data';
import { TileActionPanel } from '~/modules/moodboards/components/TileActionPanel';
import { TileActionIconButton } from '~/modules/moodboards/components/TileActionIconButton';

const HOVER_BUTTON_PADDING = 12;
const HOVER_BUTTON_PADDING_MOBILE = 8;
const HOVER_BUTTON_BORDER_RADIUS = '50%';

interface HoverButtonsImageProps {
    photoID: number;
    libraryItemId?: number;
    hideReseneTool?: boolean;
    showHint?: boolean;
    clickableLink?: string;
    imageURL: string;
    downloadLink?: string;
    taggingEnabled: boolean;
}
export const encodeDownloadLink = (link: string) => {
    return link.replaceAll('+', '%2B');
};

const HoverButtonsImage = ({
    photoID,
    libraryItemId,
    hideReseneTool = true,
    showHint = true,
    imageURL,
    downloadLink,
    taggingEnabled,
}: HoverButtonsImageProps) => {
    const canDownloadImages = useCanDownloadImages();
    const isHydrated = useHydrated();

    const {
        pickerContext: {
            state: { paintPickerActive, usageHintEnabled },
            dispatch: dispatchPaintPicker,
            openPaintPicker,
            closePaintPicker,
            setPickingProcess,
            setUsageHint,
        },
    } = useFeatureFlaggedPaintPickerContext();

    /**
     * The picture takes the browse tile's hover only while the tile refresh is
     * on. Without this the panel reached the page-builder body of a legacy
     * article or project, which is what a reader sees while the detail revamp
     * is off. Same flag the tiles read, so the two never disagree.
     */
    const tileRefreshEnabled = useFeature('revamp_2026');

    const { dispatch: dispatchProductTagging } = useProductTagging();
    const [searchParams, setSearchParams] = useSearchParams();

    const { css } = useStyles();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const professionalData = useProfessionalData();

    // hide hint after some time
    useLayoutEffect(() => {
        setTimeout(() => setUsageHint(false), HINT_ANIMATION_DURATION * 1000);
    }, [setUsageHint]);

    const buttonParams: Pick<ButtonProps, 'size' | 'color' | 'variables'> = {
        size: isMobile ? 16 : 24,
        color: 'grey',
        variables: {
            buttonRadius: HOVER_BUTTON_BORDER_RADIUS,
            buttonPadding: pxToRem(
                isMobile ? HOVER_BUTTON_PADDING_MOBILE : HOVER_BUTTON_PADDING,
            ),
            iconPadding: 0,
        },
    };

    const pickerButtonParams: Pick<
        ButtonProps,
        'size' | 'color' | 'variables'
    > = {
        size: 24,
        color: 'grey',
        variables: {
            buttonRadius: HOVER_BUTTON_BORDER_RADIUS,
            buttonPadding: pxToRem(isMobile ? 4 : HOVER_BUTTON_PADDING),
            iconPadding: 0,
        },
    };

    const openPaintPalette = () => {
        dispatchPaintPicker({
            type: 'SET_ACTIVE_IMAGE',
            payload: imageURL,
        });

        openPaintPicker();
        setPickingProcess(true);
    };

    const { isInDesignBoard } = useHandleFollow();

    const scrollToImage = (
        imageElement: HTMLElement | undefined | null,
        callback: () => void,
    ) => {
        if (imageElement) {
            // Get the image's position
            const imageRect = imageElement.getBoundingClientRect();
            const paintPickerRect = document
                .getElementById('resene-paint-picker')
                ?.getBoundingClientRect();
            const scrollToHeight = paintPickerRect
                ? window.innerHeight - paintPickerRect.height
                : window.innerHeight / 2;

            // Check if the image is below the bottom half of the screen
            if (imageRect.bottom > scrollToHeight) {
                // Scroll the window to position the image above the top half
                window.scrollBy({
                    top: imageRect.bottom - scrollToHeight,
                    behavior: 'smooth',
                });

                // window.scrollBy doesn't have any event / callback / whatever
                // so use a timeout to call openPaintPalette after scrolling
                const checkScrollCompletion = () => {
                    const currentScrollPosition = window.scrollY;
                    setTimeout(() => {
                        if (window.scrollY === currentScrollPosition) {
                            callback();
                        } else {
                            checkScrollCompletion();
                        }
                    }, 100); // Check every 100ms
                };

                checkScrollCompletion();
            } else {
                callback();
            }
        }
    };

    const canDownload = !isMobile && canDownloadImages;

    const ReseneMobileButton = () => (
        <Button
            icon={<ReseneOpenPicker />}
            onTouchStart={(e: SyntheticEvent) => {
                if (!paintPickerActive) {
                    dispatchPaintPicker({
                        type: 'SET_TOUCH_ENABLED',
                        payload: true,
                    });

                    // Find the image element two levels above the button
                    const buttonElement = e.currentTarget;
                    const imageElement =
                        buttonElement.parentElement?.parentElement
                            ?.parentElement;
                    scrollToImage(imageElement, openPaintPalette);
                } else {
                    closePaintPicker();
                }
            }}
            {...pickerButtonParams}
        />
    );

    const blurBackgroundClass =
        'before:backdrop-hack h-12 w-12 overflow-hidden rounded-full bg-gray-100/50 hover:bg-gray-100 backdrop-blur-xl';

    /**
     * The controls that are tools rather than visitor actions: taking the
     * file, and tagging a product into the photograph. Each is behind a
     * permission, so most readers see neither.
     *
     * Drawn as the panel's own controls and set in its corner beside Share, so
     * the picture carries one row rather than a panel with a second cluster
     * parked across from it. Download leads, then tagging, then Share.
     */
    const boostControl = useBoostControl();

    const toolControls = (
        <>
            {/* Boost leads the tools: it is the one that changes the picture
                itself, where the others take it or label it. */}
            {boostControl && (
                <TileActionIconButton
                    label={
                        boostControl.wasUpscaled
                            ? 'Compare boost'
                            : 'Boost Resolution'
                    }
                    icon={<Sparkles />}
                    onClick={boostControl.activate}
                />
            )}
            {canDownload && downloadLink && (
                <TileActionIconButton
                    label="Download"
                    icon={<Download2Line />}
                    href={encodeDownloadLink(downloadLink)}
                    download
                    onClick={() => {
                        tracker.log('StaffDownloadImage', {
                            url: new URL(window.location.href),
                            data: { LibraryImageID: photoID },
                        });
                    }}
                />
            )}
            {taggingEnabled && (
                <TileActionIconButton
                    label="Start tagging"
                    icon={<PriceTag3Line />}
                    onClick={() => {
                        setSearchParams(
                            { ...searchParams, tagging: 'true' },
                            { replace: false, preventScrollReset: true },
                        );
                        dispatchProductTagging({ type: 'TOGGLE_TAGGING' });
                    }}
                />
            )}
        </>
    );

    const pinterestShareUrl = isHydrated
        ? buildPinterestShareUrl({
              pageUrl: window.location.href,
              mediaUrl: imageURL,
              description: document.title,
          })
        : undefined;

    /**
     * On a project or an article the photographs carry the same hover a browse
     * tile does — Save, Pinterest, Visit site, Share, Enquire — rather than this
     * page-builder strip of icons.
     *
     * An article's enquiry goes to the practice that wrote it, about them: the
     * reader is looking at their work, and the article itself is not something
     * to quote on.
     *
     * The professional pages keep the strip; the same shell draws those, and
     * offering a practice an enquiry about themselves on their own page is
     * work the page's own CTAs already do.
     */
    const panelPageType = professionalData?.pageType;
    if (
        tileRefreshEnabled &&
        professionalData &&
        (panelPageType === 'project' || panelPageType === 'article')
    ) {
        const professional = professionalData.professional;
        // The two pages hand this hook differently-shaped professionals — the
        // project route's own GraphQL type, and the article route's mapped one,
        // which spells its link in lower case and may carry no website at all.
        const professionalLink =
            'Link' in professional ? professional.Link : professional.link;
        const professionalWebsite =
            'Website' in professional
                ? professional.Website
                : professional.website;
        return (
            // Takes no pointer events of its own — the panel's pills take
            // theirs back — so a click still reaches the picture.
            <div className="hover-buttons-image pointer-events-none absolute inset-0">
                <TileActionPanel
                    shareLeading={toolControls}
                    pinId={photoID}
                    libraryItemId={libraryItemId ?? photoID}
                    pinType="PhotoPin"
                    enquiry={{
                        itemType:
                            panelPageType === 'article'
                                ? 'professional'
                                : 'project',
                        siteTreeID:
                            panelPageType === 'article'
                                ? professional.ID
                                : (libraryItemId ?? photoID),
                        professionalId: professional.ID,
                        professionalName: professional.Title,
                        professionalLink: professionalLink,
                    }}
                    visitWebsite={
                        professionalWebsite
                            ? {
                                  website: professionalWebsite,
                                  libraryItemID: libraryItemId ?? photoID,
                                  professionalID: professional.ID,
                                  professionalTitle: professional.Title,
                              }
                            : null
                    }
                    share={{
                        url:
                            typeof window === 'undefined'
                                ? ''
                                : window.location.href,
                        title: professional.Title,
                    }}
                    // The panel pins the tile's own image by default; a gallery
                    // photograph is the picture on screen, not a thumbnail of
                    // it, so point the pin at the full-size source instead.
                    pinterest={{
                        mediaUrl: imageURL,
                        professionalID: professional.ID,
                        libraryImageID: photoID,
                    }}
                />
            </div>
        );
    }

    return (
        <Flex
            className={`${css(
                S.HoverButtonsImageContainer,
            )} hover-buttons-image`}
            vAlign="start"
        >
            {pinterestShareUrl && (
                <HoverAction
                    label="Save to Pinterest"
                    action={
                        <Button
                            as="a"
                            icon={<PinterestFill />}
                            href={pinterestShareUrl}
                            target="_blank"
                            rel="noreferrer"
                            {...buttonParams}
                            onClick={(e) => {
                                e.stopPropagation();
                                void tracker.log('PinterestShare', {
                                    url: new URL(window.location.href),
                                    label: `Pinterest:${document.title}`,
                                    data: {
                                        ProfessionalID:
                                            professionalData?.professional.ID ??
                                            0,
                                        LibraryImageID: photoID,
                                        Source: 'HoverButtonsImage desktop',
                                    },
                                });
                            }}
                            className={`${blurBackgroundClass} text-black`}
                        />
                    }
                />
            )}
            {!hideReseneTool && (
                <HoverAction
                    label="Show Resene paint ideas"
                    action={
                        showHint ? (
                            <motion.div
                                initial={false}
                                className={css(S.HoverButtonsHintContainer)}
                                animate={
                                    usageHintEnabled ? 'hintOn' : 'hintOff'
                                }
                                variants={S.hintAnimation}
                            >
                                <motion.div
                                    className={css(S.HoverButtonsHintLabel)}
                                    variants={S.hintLabelAnimation as Variants}
                                >
                                    Tap to use Colour selector
                                </motion.div>
                                <ReseneMobileButton />
                            </motion.div>
                        ) : (
                            <ReseneMobileButton />
                        )
                    }
                />
            )}
            {taggingEnabled && (
                <HoverAction
                    label="Start tagging"
                    action={
                        <Button
                            icon={<PriceTag3Line />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSearchParams(
                                    {
                                        ...searchParams,
                                        tagging: 'true',
                                    },
                                    {
                                        replace: false,
                                        preventScrollReset: true,
                                    },
                                );

                                dispatchProductTagging({
                                    type: 'TOGGLE_TAGGING',
                                });
                            }}
                            {...buttonParams}
                            className={blurBackgroundClass}
                        />
                    }
                />
            )}
            <HoverAction
                label={BOARD_TERMS.saveTo}
                action={
                    <FollowSelector
                        trigger={
                            <Button
                                icon={
                                    isInDesignBoard(photoID, 'PhotoPin') ? (
                                        <HeartFill />
                                    ) : (
                                        <HeartLine />
                                    )
                                }
                                {...buttonParams}
                                className={blurBackgroundClass}
                            />
                        }
                        pinId={photoID}
                        libraryItemId={libraryItemId}
                        pinType="PhotoPin"
                    />
                }
            />
            {boostControl && (
                <HoverAction
                    label={
                        boostControl.wasUpscaled
                            ? 'Compare boost'
                            : 'Boost Resolution'
                    }
                    action={
                        <Button
                            icon={<Sparkles />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                boostControl.activate();
                            }}
                            {...buttonParams}
                            className={`${blurBackgroundClass} text-black`}
                        />
                    }
                />
            )}
            {canDownload && downloadLink && (
                <HoverAction
                    label={'Download'}
                    action={
                        <Button
                            as="a"
                            icon={<Download2Line />}
                            href={encodeDownloadLink(downloadLink)}
                            download
                            {...buttonParams}
                            onClick={(e) => {
                                e.stopPropagation();
                                tracker.log('StaffDownloadImage', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        // ImageID
                                        ExtraData: photoID,
                                    },
                                });
                            }}
                            className={blurBackgroundClass}
                        />
                    }
                />
            )}
        </Flex>
    );
};

export default HoverButtonsImage;
